import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, FileText, BookOpen, Settings, 
  Loader2, Users, Percent, DownloadCloud, Bell
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Sub-components
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminProducts from '../components/admin/AdminProducts';
import AdminOrders from '../components/admin/AdminOrders';
import AdminQuotes from '../components/admin/AdminQuotes';
import AdminClients from '../components/admin/AdminClients';
import AdminBlog from '../components/admin/AdminBlog';
import AdminSettings from '../components/admin/AdminSettings';
import AdminNotifications from '../components/admin/AdminNotifications';
import AdminPromoCodes from '../components/admin/AdminPromoCodes';
import AdminWaitlist from '../components/admin/AdminWaitlist';

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth', { state: { from: '/admin' } });
      return;
    }
    
    if (user.role !== 'admin') {
      navigate('/');
      console.log('Error:', 'Accès refusé');
      return;
    }
  }, [user, authLoading, navigate]);

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'products', label: 'Produits', icon: Package },
    { id: 'orders', label: 'Commandes', icon: ShoppingCart },
    { id: 'quotes', label: 'Devis', icon: FileText },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'waitlist', label: 'Liste d\'attente', icon: Bell },
    { id: 'promo', label: 'Codes Promo', icon: Percent },
    { id: 'blog', label: 'Blog', icon: BookOpen },
    { id: 'settings', label: 'Paramètres', icon: Settings },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-fgs-bg" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen bg-fgs-surface border-r border-fgs-border p-4 fixed left-0 top-0">
          {/* Header */}
          <div className="mb-6">
            <h1 className="font-heading text-xl font-bold text-white">FGS Admin</h1>
            <p className="text-xs text-fgs-text-muted">Panneau d'administration</p>
          </div>
          
          {/* Notifications Button */}
          <div className="mb-6 px-1">
            <AdminNotifications onNavigate={setActiveTab} />
          </div>

          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-fgs-primary text-white'
                    : 'text-fgs-text-muted hover:bg-fgs-surface-highlight hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
            <button
              onClick={() => navigate('/admin/import-visiotech')}
              data-testid="nav-visiotech-import"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors text-fgs-text-muted hover:bg-fgs-surface-highlight hover:text-white"
            >
              <DownloadCloud className="w-5 h-5" />
              <span className="text-sm font-medium">Import Visiotech</span>
            </button>
          </nav>

          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-fgs-bg rounded-lg p-3">
              <p className="text-xs text-fgs-text-muted">Connecté en tant que</p>
              <p className="text-sm text-white font-medium truncate">{user?.name}</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 ml-64 p-8">
          {activeTab === 'dashboard' && <AdminDashboard />}
          {activeTab === 'products' && <AdminProducts />}
          {activeTab === 'orders' && <AdminOrders />}
          {activeTab === 'quotes' && <AdminQuotes />}
          {activeTab === 'clients' && <AdminClients />}
          {activeTab === 'waitlist' && <AdminWaitlist />}
          {activeTab === 'promo' && <AdminPromoCodes />}
          {activeTab === 'blog' && <AdminBlog />}
          {activeTab === 'settings' && <AdminSettings />}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
